<!-- A snackbar to confirm/abort a selection of SpotEntries/TickerEntries -->

<template>
  <v-snackbar
    v-model="show"
    class="
      entryselectionoverlay
      fixed-snackbar-z-index
      v-snackbar--auto-height v-snackbar--no-top-left-right-padding
    "
    color="grey lighten-4"
    multi-line
    vertical
    :class="$desktopModeStyles"
    :timeout="0"
    :dark="false"
  >
    <v-layout
      column
      class="pl-3 pr-2 pt-2"
    >
      <v-layout align-center>
        <v-layout
          align-center
          class="black--text"
        >
          <v-icon class="mr-2">
            {{ titleIcon }}
          </v-icon>
          <span class="nowrap">{{ title }}</span>
        </v-layout>
        <!-- Button to abort -->
        <v-btn
          class="ml-2 mr-0 my-0"
          :disabled="loading"
          flat
          icon
          color="black"
          style="min-width: 36px; max-height: 36px"
          :title="$t('abort')"
          @click="$emit('aborted')"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-layout>
      <v-layout
        align-end
        class="full-width mt-3"
      >
        <v-btn
          v-if="entries.length > 2"
          flat
          class="my-0"
          @click="setAllEntriesSelected(!allEntriesSelected)"
        >
          <v-layout align-center>
            <v-icon
              v-if="!allEntriesSelected"
              color="msr"
            >
              check_box_outline_blank
            </v-icon>
            <v-icon
              v-else
              color="msr"
            >
              check_box
            </v-icon>
            <span
              class="ml-2 grey--text text--darken-3"
              style="text-transform: none"
            >{{ $t('allCapitalized') }}</span>
          </v-layout>
        </v-btn>
        <v-spacer />
        <!-- Most modes use a single button -->
        <btn-ok
          v-if="modeUsesSingleButton"
          :id="'overlay-btn-confirm-' + mode"
          round
          :icon="modeSingleConfirmButtonIcon"
          :label="modeSingleConfirmButtonLabel"
          label-with-icon
          class="fixed-snackbar-btn"
          :disabled="!selectedEntries.length"
          :loading="loading"
          data-cy="entry-selection-overlay-confirm-btn"
          @click="$emit('confirmed')"
        />
        <!-- Hotrotating uses two buttons to return a specific result. -->
        <span v-else-if="modeUsesTwoButtons">
          <v-layout nowrap>
            <v-btn
              round
              :dark="selectedEntries.length > 0"
              outline
              class="fixed-snackbar-btn msr--text"
              :disabled="!selectedEntries.length"
              :loading="loading"
              @click="$emit('confirmed', false)"
            >
              <v-icon medium>star_outline</v-icon>
            </v-btn>
            <v-btn
              round
              :dark="selectedEntries.length > 0"
              outline
              class="fixed-snackbar-btn msr--text"
              :class="{'smaller-btn-min-width': needSmallerButtons}"
              :disabled="!selectedEntries.length"
              :loading="loading"
              @click="$emit('confirmed', true)"
            >
              <v-icon medium>star</v-icon>
            </v-btn>
          </v-layout>
        </span>
        <v-spacer />
      </v-layout>
    </v-layout>
  </v-snackbar>
</template>

<script>
import BtnOk from '@/components/ButtonOk';

export default {
  components: {
    BtnOk,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    titleIcon: {
      type: String,
      required: true,
    },
    /**
     * Show different buttons depending on mode of batch action.
     *
     * Expected modes are : 'timing', 'removing', 'hotrotating'
     */
    mode: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    /* A list of entries (not yet filtered by .selected) */
    entries: {
      type: Array[Object],
      required: true,
    },
    selectAllEntriesOnClose: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    selectableEntries() {
      return this.entries.filter((entry) => entry.selectable);
    },
    selectedEntries() {
      return this.selectableEntries.filter((entry) => entry.selected);
    },
    allEntriesSelected() {
      return this.selectedEntries.length === this.selectableEntries.length;
    },
    modeUsesSingleButton() {
      return (
        this.mode === 'timing' ||
        this.mode === 'removing' ||
        this.mode === 'reactivating' ||
        this.mode === 'overlayediting'
      );
    },
    modeUsesTwoButtons() {
      return this.mode === 'hotrotating';
    },
    modeSingleConfirmButtonIcon() {
      return this.mode === 'timing' || this.mode === 'overlayediting'
        ? 'arrow_forward'
        : 'check';
    },
    modeSingleConfirmButtonLabel() {
      return this.mode === 'removing'
        ? this.$t('confirm')
        : this.$t('goForward');
    },
  },
  watch: {
    show: function (val) {
      if (!val && this.selectAllEntriesOnClose) {
        this.setAllEntriesSelected(true);
      }
    },
  },
  methods: {
    setAllEntriesSelected(selected) {
      this.selectableEntries.forEach((entry) => {
        entry.selected = selected;
      });
    },
  },
};
</script>

<style scoped>
.v-snackbar--auto-height >>> .v-snack__content {
  height: auto;
}

.entryselectionoverlay >>> .v-snack__content .v-btn.fixed-snackbar-btn {
  flex-grow: 1;
  margin: 0 0 0 8px !important;
}
.entryselectionoverlay.v-snackbar--no-top-left-right-padding
  >>> .v-snack__content {
  padding-top: 0;
  padding-left: 0;
  padding-right: 0;
}
</style>

<style>
/**
 *  A dialog's backdrop (.v-overlay--active) has a z-index of 201 as of
 *  Vuetify 1.5, snackbars have one of 1000. Reduce it so the snackbar
 *  is behind any dialogs.
 */
.fixed-snackbar-z-index {
  z-index: 200;
}
</style>



// WEBPACK FOOTER //
// src/components/EntrySelectionOverlay.vue